import React from "react"
import styled from "styled-components"
import {
  Copyright,
  Header, HeroImage,
  HR,
  LinkText, LoginButton,
  Main,
  MainInnerWrap, PageTitle,
  Paragraph,
  People,
  Person, PersonSubTitle, PersonTitle,
  Portrait, Row,
  Section,
  SectionTitle
} from "../Landing";

// 1) Grid wrapper
const HeroGrid = styled.div`
  display: grid;
  gap: 3.75rem;            /* matches your HeroImage bottom margin */
  
  /* mobile: one column */
  grid-template-columns: 1fr;

  /* desktop: two equal columns */
  @media (min-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

// 2) Optional: remove bottom margin because grid gap already gives spacing
const SideHeroImage = styled(HeroImage)`
  margin-bottom: 0;
`

const Icon = styled.img`
  width: clamp(20rem, 45vw, 100rem);
  height: auto;
  flex-shrink: 0;
  margin-right: 1rem;
`;

const HeaderRow = styled(Row)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  margin: 2rem 0;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  th, td {
    padding: 0.5rem 0.75rem;
    border: 1px solid #e0e0e0;
    vertical-align: top;
    line-height: 1.6;          /* ↑ more breathing room between lines */
  }

  thead th {
    background: #fafafa;
    font-weight: 600;
    font-size: 1rem;
    text-align: center;
    color: #333;
  }

  tbody tr:nth-child(even) {
    background: #fcfcfc;
  }

  td:first-child {
    width: 8rem;
    white-space: nowrap;
    font-weight: 500;
    background: #f9f9f9;
  }

  /* remove bullets and tighten up list spacing */
  td ul {
    //list-style: none;
    margin: 0 0 0 1rem;
    padding: 0;
  }
  td ul li {
    margin-bottom: 0.5rem;    /* space between items */
    /* no bullets, so no extra left padding needed */
  }
`
const SectionTitleWithLink = styled(SectionTitle)`
  margin-bottom: 0.5rem;
`

const CenteredTitleLink = styled(LinkText)`
  display: block;
  text-align: center;
  margin-bottom: 1.5rem;
`

export default function SightLanding() {
  return (
    <Main>
      <MainInnerWrap>
        <Header>
          <HeaderRow>
            <TitleGroup>
              <Icon
                src="/images/sight_2_umich_w_words.svg"
                alt="MI SIGHT logo"
              />
              {/*<InlineTitle>Michigan SIGHT Program</InlineTitle>*/}
            </TitleGroup>

            <LoginButton href="/sight-timeline">
              Login Implementation Toolkit
            </LoginButton>
          </HeaderRow>
        </Header>
        <HeroGrid>
          <HeroImage src="/images/eye-exam-sight-1.jpg" />
          <HeroImage src="/images/eye-exam-sight-2.jpg" />
        </HeroGrid>
        <Section>
          <SectionTitleWithLink>What is the MI SIGHT Program?</SectionTitleWithLink>
          <CenteredTitleLink href="https://drive.google.com/file/d/1OX9aFfSkxdmC4FLBZGqf5ktdEmFeE08j/view" target="_blank">
            ¿Qué es el programa MI SIGHT?
          </CenteredTitleLink>
          <Paragraph>To preserve sight as we age, people need to get their eyes checked for eye disease so they can get treatment if needed. And yet, many people have a hard time getting in to see an eye doctor. The Michigan Screening and Intervention for Glaucoma and Eye Health through Telemedicine (MI-SIGHT) Program uses a technology-based eye disease testing screening protocol. The tests are reviewed by an eye doctor with expertise in telemedicine to identify people who need further care for eye diseases. The MI-SIGHT Program also helps people buy affordable and stylish glasses online.</Paragraph>
        </Section>
        <Section>
          <SectionTitle>What is the MI SIGHT Program Trial?</SectionTitle>
          <Paragraph>To understand whether the MI-SIGHT Program improves glaucoma and eye disease detection, we will compare the MI-SIGHT Program to a standard comprehensive examination with an eye doctor in a randomized controlled trial. A randomized controlled trial is a study in which people are put into groups by chance. Some receive the new technology-based exam, and others get the standard exam. This is the best research method to understand whether a new technology-based exam is more effective compared to the standard exam. After the completion of the MI-SIGHT Program randomized controlled trial, we will know whether to recommend the MI-SIGHT Program as the new “standard of care” to policymakers for glaucoma and eye disease screening.  To find out more about the trial, visit <LinkText href="https://clinicaltrials.gov/study/NCT06934577?term=NCT06934577&rank=1" target="_blank">ClinicalTrials.gov</LinkText>.</Paragraph>
        </Section>
        <Section>
          <SectionTitle>Why participate in the MI SIGHT Program Trial?</SectionTitle>
          <Paragraph>Participating in research may help you and may help many others. If you join the MI-SIGHT Program trial, you will get both a regular eye exam and the new technology-based eye exam. By participating in the trial, you will help researchers and health policy makers understand whether paying for technology-based eye disease screening would help more people know they have glaucoma or other eye diseases earlier so they can get treatments before vision loss. Your participation is voluntary, and you may decide not to participate at any time. If you do not want to participate, you may receive a standard eye exam through the Hamilton clinic.</Paragraph>
        </Section>
        <Section>
          <SectionTitle>MI SIGHT Program Publications and Presentations</SectionTitle>
          <Paragraph><LinkText href="/sight-publications" target="_blank">Click to see the SIGHT Program Publications</LinkText></Paragraph>
        </Section>
        <Section>
          <SectionTitle>How can I participate in the MI SIGHT Program Trial?</SectionTitle>
          <Paragraph>To participate in the program, you need to be 18 years old or older and not pregnant.
            You will be randomly assigned to 1 of 2 groups. Both groups will complete health surveys. The optometrist visit will be billed to your insurance.</Paragraph>
          <TableWrapper>
            <Table>
              <thead>
              <tr>
                <th></th>
                <th>Visit 1 – Initial visit</th>
                <th>Visit 2 – Two weeks later</th>
                <th>Visit 3 – Six weeks later</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Group 1</td>
                <td>
                  <ul>
                    <li>Optometrist exam</li>
                    <li>Technology-based exam</li>
                    <li>You will receive $35</li>
                    <li>2 hours</li>
                  </ul>
                </td>
                <td></td>
                <td>
                  <ul>
                    <li>Survey</li>
                    <li>You can order low-cost glasses</li>
                    <li>You will receive $15</li>
                    <li>10 minutes</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>Group 2</td>
                <td>
                  <ul>
                    <li>Technology-based exam</li>
                    <li>You can order low-cost glasses</li>
                    <li>You will receive $10</li>
                    <li>1.5 hours</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>Optometrist exam</li>
                    <li>Pick up glasses, if ordered</li>
                    <li>You will receive $35</li>
                    <li>30 minutes</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>Surveys</li>
                    <li>You will receive $15</li>
                    <li>10 minutes</li>
                  </ul>
                </td>
              </tr>
              </tbody>
            </Table>
          </TableWrapper>
        </Section>
        <HR/>
        <Section>
          <SectionTitle>Study Location</SectionTitle>
          <Paragraph><strong>Hamilton Community Health Clinic</strong><br />2900 N. Saginaw
            Flint, MI 48505<br/>Phone: 810-309-9378<br />Email: <LinkText href="mailto:hamiltoncommunitysightprogram@gmail.com">hamiltoncommunitysightprogram@gmail.com</LinkText></Paragraph>
        </Section>
        <Section>
          <SectionTitle>Our Team</SectionTitle>
          <People>
            <Person>
              <Portrait src="/images/people/PANC-sight.png" />
              <PersonTitle>Paula Anne Newman-Casey, MD, MS</PersonTitle>
              <PersonSubTitle>Associate Professor of Ophthalmology and Visual Sciences, University of Michigan Medical School</PersonSubTitle>
              <PersonSubTitle>Strategic Director, Kellogg Eye Center for eHealth</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Woodward-sight.jpg" />
              <PersonTitle>Maria Woodward, MD, MS</PersonTitle>
              <PersonSubTitle>Associate Professor of Ophthalmology and Visual Sciences, University of Michigan Medical School</PersonSubTitle>
              <PersonSubTitle>Strategic Director, Kellogg Eye Center for eHealth</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Angela-sight.jpg" />
              <PersonTitle>Angela R. Elam, MD</PersonTitle>
              <PersonSubTitle>Assistant Professor of Ophthalmology and Visual Sciences, University of Michigan Medical School</PersonSubTitle>
            </Person>
          </People>
          <People>
            <Person>
              <Portrait src="/images/people/Bicket-sight.jpg" />
              <PersonTitle>Amanda Bicket, MD</PersonTitle>
              <PersonSubTitle>Assistant Professor of Ophthalmology and Visual Sciences, University of Michigan Medical School</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Cecilia-sight.png" />
              <PersonTitle>Cecilia Rutherford, MSN, RN</PersonTitle>
              <PersonSubTitle>Chief Operating Officer, Hamilton Community Health Network</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Giacalone-sight.png" />
              <PersonTitle>Michael Giacalone, Jr., MD</PersonTitle>
              <PersonSubTitle>CMO, Hamilton Community Health Network</PersonSubTitle>
            </Person>
          </People>
          <People>
            <Person>
              <Portrait src="/images/people/Pierce-sight.png" />
              <PersonTitle>Clarence Pierce, MS</PersonTitle>
              <PersonSubTitle>Chief Executive Officer, Hamilton Community Health Network</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Kirk-sight.png" />
              <PersonTitle>Kirk D. Smith, MHSA</PersonTitle>
              <PersonSubTitle>Chief Administrative Officer, Hamilton Community Health Network</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Hutton.png" />
              <PersonTitle>David Hutton, PhD, MS</PersonTitle>
              <PersonSubTitle>Professor of Health Management and Policy, UM</PersonSubTitle>
            </Person>
          </People>
          <People>
            <Person>
              <Portrait src="/images/people/Heisler.jpg" />
              <PersonTitle>Michele Heisler, MD, MPA</PersonTitle>
              <PersonSubTitle>Kutsche Memorial Research Professor of Internal Medicine, Professor of Internal Medicine and Health Behavior and Health Equity, UM</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Nwanyanwu.jpg" />
              <PersonTitle>Kristen Nwanyanwu, MD, MBA, MHS</PersonTitle>
              <PersonSubTitle>Associate Professor of Ophthalmology and Visual Science, Yale School of Medicine</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/David-sight.jpg" />
              <PersonTitle>David Bush, OD</PersonTitle>
              <PersonSubTitle>Optometrist - Hamilton Community Health Network</PersonSubTitle>
            </Person>
          </People>
          <People>
            <Person>
              <Portrait src="/images/people/Winter-sight.png" />
              <PersonTitle>Suzanne Winter, MS, CCRC, ACRP-PM </PersonTitle>
              <PersonSubTitle>Clinical Research Project Manager</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Thy-sight.jpg" />
              <PersonTitle>Thy Nguyen, BS</PersonTitle>
              <PersonSubTitle>Research Administrative Associate</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Jade-sight.png" />
              <PersonTitle>Jade Livingston, BS</PersonTitle>
              <PersonSubTitle>Clinical Research Coordinator - Technician</PersonSubTitle>
            </Person>
          </People>
          <People>
            <Person>
              <Portrait src="/images/people/Mildred-sight.JPG" />
              <PersonTitle>Mildred Silva-Zuccaro, MBBS</PersonTitle>
              <PersonSubTitle>Clinical Subjects Coordinator</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Phalatha-sight.jpg" />
              <PersonTitle>Phalatha McHaney-Conner, BS, CCRC</PersonTitle>
              <PersonSubTitle>Clinical Research Coordinator - Intermediate</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Frank-sight.png" />
              <PersonTitle>Francisco Marino, MD</PersonTitle>
              <PersonSubTitle>Clinical Research Coordinator, Assistant - Ophthalmic Technician</PersonSubTitle>
            </Person>
          </People>
          <People>
            <Person>
              <Portrait src="/images/people/Bell-sight.JPG" />
              <PersonTitle>Tracey Bell</PersonTitle>
              <PersonSubTitle>Customer Service Representative, Hamilton Community Health Network</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Zalecki-sight.jpeg" />
              <PersonTitle>Jodi Zalecki</PersonTitle>
              <PersonSubTitle>Certified Ophthalmic Assistant, Hamilton Community Health Network</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Niziol-sight.jpg" />
              <PersonTitle>Leslie Niziol, MS</PersonTitle>
              <PersonSubTitle>Statistician Lead, UM</PersonSubTitle>
            </Person>
          </People>
          <People>
            <Person>
              <Portrait src="/images/people/Lu-sight.jpg" />
              <PersonTitle>Ming-Chen (Amy) Lu, MS</PersonTitle>
              <PersonSubTitle>Statistician Senior, UM</PersonSubTitle>
            </Person>
          </People>
        </Section>
        <HR/>
        <Section>
          <SectionTitle fontSize="2rem">The Vision Health Initiative</SectionTitle>
          <Paragraph>
            <LinkText href="https://www.cdc.gov/vision-health/" target="_blank">The Vision Health Initiative (VHI)</LinkText> at the Centers for Disease Control and Prevention aims to enhance public health surveillance and research that provides the basis for effective public health programs and policies to reduce the burden of vision loss. Estimates of the U.S. economic burden of vision loss nationally and by state can be examined on CDC’s <LinkText href="https://www.cdc.gov/vision-health/php/economics-explorer/index.html" target="_blank">Vision Loss Economic Explorer</LinkText>. The MI-SIGHT Trial is funded through the Vision Health Initiative.
          </Paragraph>
        </Section>
        <Copyright>© 2025 The University of Michigan – All Rights Reserved</Copyright>
      </MainInnerWrap>
    </Main>
  )
}



// WEBPACK FOOTER //
// ./src/pages/SightLanding/index.js